import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, RefreshCw, Plus, Ban, AlertTriangle, KeyRound } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  rootFetch, stepUpChallenge, isExactAddress, isValidExpiryDays,
  DEFAULT_EXPIRY_DAYS, MAX_EXPIRY_DAYS, PURPOSE_PLATFORM_ADMIN_INVITATION,
  purposeLabel, recipientStatus, formatTimestamp,
} from '../../lib/rootSecurity';

// STAGING EXTERNAL RECIPIENTS — Root only.
//
// Staging refuses to e-mail anyone outside a deploy-controlled allowlist, which is correct and stays
// that way. This is the governed exception: Root approves ONE exact address, for ONE purpose, for a
// finite period, revocably. It never widens the static allowlist.
//
// The whole section — including the LIST — is Root-gated rather than read-only-for-SuperAdmin. During
// an engagement the SuperAdmins on this plane include the penetration testers, so showing one tester
// the other approved addresses would leak the engagement roster to exactly the population it is
// scoped around.
//
// Rendered as a section inside the existing Security & Compliance page. Not a new top-level area.

const EMPTY_FORM = {
  email: '', organization: '', reason: '',
  purpose: PURPOSE_PLATFORM_ADMIN_INVITATION, days: String(DEFAULT_EXPIRY_DAYS),
};

const StagingExternalRecipients = ({ rootStatus }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [busy, setBusy] = useState(null);

  // A refused action parked until Root proves presence. Holds the retry, so a satisfied challenge
  // resumes the exact operation the operator asked for rather than making them start again.
  const [pending, setPending] = useState(null);   // { action, retry, label }
  const [code, setCode] = useState('');
  const [stepUpBusy, setStepUpBusy] = useState(false);
  const [stepUpError, setStepUpError] = useState(null);

  const mayManage = Boolean(rootStatus && rootStatus.can_manage_staging_recipients);

  const load = useCallback(async () => {
    if (!mayManage) return;
    setLoading(true); setError(null);
    try {
      const data = await rootFetch('/api/v1/super-admin/security/staging-recipients');
      setRows((data && data.recipients) || []);
    } catch (err) {
      setError(err.status ? `HTTP ${err.status}` : 'network error');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [mayManage]);

  // Only ever fetched when the server has said this caller is Root. An ordinary SuperAdmin never
  // issues the request at all, so the roster is not merely hidden — it is never sent to them.
  useEffect(() => { load(); }, [load]);

  // One place performs a guarded mutation. On MFA_STEP_UP_REQUIRED it parks the retry and prompts;
  // on any other refusal it reports honestly and does NOT offer a code, because a code would not help.
  const guarded = async (label, action, fn) => {
    setError(null); setNotice(null); setBusy(action);
    try {
      const out = await fn();
      setPending(null); setCode(''); setStepUpError(null);
      return out;
    } catch (err) {
      const challenge = stepUpChallenge(err);
      if (challenge) {
        setPending({ action: challenge.action || action, retry: fn, label });
        setStepUpError(null);
      } else {
        setError(typeof err.detail === 'string' ? err.detail : (err.status ? `HTTP ${err.status}` : 'network error'));
      }
      return null;
    } finally {
      setBusy(null);
    }
  };

  const submitAdd = async () => {
    const email = form.email.trim().toLowerCase();
    if (!isExactAddress(email)) {
      setError('Enter one exact e-mail address (no wildcards or bare domains).'); return;
    }
    if (!form.reason.trim()) { setError('A reason is required — it is recorded in the audit log.'); return; }
    if (!isValidExpiryDays(form.days)) { setError(`Expiry must be between 1 and ${MAX_EXPIRY_DAYS} days.`); return; }

    const payload = {
      email,
      organization: form.organization.trim(),
      reason: form.reason.trim(),
      purpose: form.purpose,
      days: Number(form.days),
    };
    const out = await guarded(`Approve ${email}`, 'add', () => rootFetch(
      '/api/v1/super-admin/security/staging-recipients',
      { method: 'POST', body: JSON.stringify(payload) },
    ));
    if (out) {
      setNotice(`Approved ${email} until ${formatTimestamp(out.recipient && out.recipient.expires_at)}.`);
      setAddOpen(false); setForm({ ...EMPTY_FORM });
      await load();
    }
  };

  const revoke = async (row) => {
    if (!window.confirm(`Revoke approval for ${row.email}? Staging will stop delivering platform mail to this address immediately. The record is retained for audit.`)) return;
    const out = await guarded(`Revoke ${row.email}`, `revoke-${row.reference}`, () => rootFetch(
      `/api/v1/super-admin/security/staging-recipients/${encodeURIComponent(row.reference)}/revoke`,
      { method: 'POST' },
    ));
    if (out) { setNotice(`Revoked ${row.email}.`); await load(); }
  };

  // Verify the code through the platform step-up route, then replay the parked action EXACTLY once.
  const submitStepUp = async () => {
    if (!/^\d{6}$/.test(code.trim())) { setStepUpError('Enter the 6-digit code from your authenticator.'); return; }
    setStepUpBusy(true); setStepUpError(null);
    try {
      await rootFetch('/api/v1/super-admin/security/step-up',
        { method: 'POST', body: JSON.stringify({ code: code.trim() }) });
    } catch (err) {
      setStepUpError(typeof err.detail === 'string' ? err.detail : 'That code was not accepted.');
      setStepUpBusy(false);
      return;
    }
    const parked = pending;
    setPending(null); setCode(''); setStepUpBusy(false);
    if (!parked) return;
    try {
      await parked.retry();
      setNotice(`${parked.label} — completed.`);
      await load();
      setAddOpen(false); setForm({ ...EMPTY_FORM });
    } catch (err) {
      setError(typeof err.detail === 'string' ? err.detail : (err.status ? `HTTP ${err.status}` : 'network error'));
    }
  };

  // NOT_EXPOSED for an ordinary SuperAdmin. No heading, no empty state, no disabled control — a
  // control that merely 403s tells the caller the capability exists and invites them to probe it.
  if (!mayManage) return null;

  return (
    <Card className="bg-white shadow-md" data-testid="staging-external-recipients">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-purple-600" />
              Staging External Recipients
            </CardTitle>
            <p className="text-xs text-gray-500 mt-1">
              Root only. Approves one exact address to receive a platform-admin invitation on staging,
              for a finite period. The deploy allowlist is unchanged.
            </p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={load} disabled={loading || !!busy}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
            </Button>
            <Button size="sm" onClick={() => { setAddOpen(true); setError(null); }} disabled={!!busy}>
              <Plus className="h-4 w-4 mr-2" />Add recipient
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {error && (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-red-50 border border-red-200">
            <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        {notice && (
          <div className="p-3 rounded-lg bg-green-50 border border-green-200">
            <p className="text-sm text-green-800">{notice}</p>
          </div>
        )}

        {pending && (
          <div className="p-3 rounded-lg bg-blue-50 border border-blue-200" data-testid="root-step-up">
            <p className="text-sm text-blue-900 flex items-center gap-2 mb-2">
              <KeyRound className="h-4 w-4" />
              {pending.label} needs a fresh authenticator code before it can proceed.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                className="border rounded-md px-3 py-2 text-sm w-40"
                inputMode="numeric" maxLength={6} placeholder="000000"
                aria-label="Authenticator code"
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              />
              <Button size="sm" onClick={submitStepUp} disabled={stepUpBusy}>
                {stepUpBusy ? 'Verifying…' : 'Verify and continue'}
              </Button>
              <Button size="sm" variant="outline" disabled={stepUpBusy}
                onClick={() => { setPending(null); setCode(''); setStepUpError(null); }}>
                Cancel
              </Button>
            </div>
            {stepUpError && <p className="text-xs text-red-700 mt-2">{stepUpError}</p>}
          </div>
        )}

        {addOpen && (
          <div className="p-4 rounded-lg border bg-gray-50 space-y-3" data-testid="add-recipient-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="text-xs text-gray-600">E-mail address
                <input className="mt-1 w-full border rounded-md px-3 py-2 text-sm" type="email"
                  placeholder="person@example.com" aria-label="Recipient e-mail"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </label>
              <label className="text-xs text-gray-600">Organization
                <input className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                  placeholder="Engagement partner" aria-label="Organization"
                  value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} />
              </label>
              <label className="text-xs text-gray-600">Purpose
                <select className="mt-1 w-full border rounded-md px-3 py-2 text-sm" aria-label="Purpose"
                  value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })}>
                  <option value={PURPOSE_PLATFORM_ADMIN_INVITATION}>
                    {purposeLabel(PURPOSE_PLATFORM_ADMIN_INVITATION)}
                  </option>
                </select>
              </label>
              <label className="text-xs text-gray-600">Expires in (days, max {MAX_EXPIRY_DAYS})
                <input className="mt-1 w-full border rounded-md px-3 py-2 text-sm" type="number"
                  min="1" max={MAX_EXPIRY_DAYS} aria-label="Expiry in days"
                  value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} />
              </label>
            </div>
            <label className="text-xs text-gray-600 block">Reason (recorded in the audit log)
              <input className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                placeholder="Authorized staging penetration test" aria-label="Reason"
                value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </label>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => { setAddOpen(false); setForm({ ...EMPTY_FORM }); setError(null); }}>
                Cancel
              </Button>
              <Button size="sm" onClick={submitAdd} disabled={busy === 'add'}>
                {busy === 'add' ? 'Approving…' : 'Approve recipient'}
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-gray-500 py-4">Loading approvals…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">
            No external recipients approved. Staging delivers only to the deploy allowlist.
          </p>
        ) : (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {['Email', 'Organization', 'Purpose', 'Reason', 'Added by', 'Added at', 'Expires', 'Status', 'Actions'].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => {
                const st = recipientStatus(r.status);
                return (
                  <tr key={r.reference} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-900">{r.email}</td>
                    <td className="px-3 py-2 text-gray-700">{r.organization || '—'}</td>
                    <td className="px-3 py-2 text-gray-700">{purposeLabel(r.purpose)}</td>
                    <td className="px-3 py-2 text-gray-700">{r.reason || '—'}</td>
                    <td className="px-3 py-2 text-gray-700">{r.created_by || '—'}</td>
                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{formatTimestamp(r.created_at)}</td>
                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{formatTimestamp(r.expires_at)}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${st.cls}`}>{st.label}</span>
                    </td>
                    <td className="px-3 py-2">
                      {r.status === 'active' ? (
                        <Button size="sm" variant="outline"
                          className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                          disabled={!!busy} onClick={() => revoke(r)}>
                          <Ban className="h-3 w-3 mr-1" />
                          {busy === `revoke-${r.reference}` ? '…' : 'Revoke'}
                        </Button>
                      ) : (
                        <span className="text-xs text-gray-400">
                          {r.revoked_at ? `Revoked ${formatTimestamp(r.revoked_at)}` : 'No longer active'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </CardContent>
    </Card>
  );
};

export default StagingExternalRecipients;
